import { AsyncMultiSelectField, CharField, CheckboxField, LinkField, SwitchField } from "@inmagik/magik-ui"
import { FieldArray, getIn, useFormikContext } from "formik"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { Col, Row } from "react-bootstrap"
import { WysiwygField } from "../../components/WysiwygField"
import { useCreateKeyword, useKeywordLazy, useKeywordsLazy } from "../../hooks/keywords"
import { useMunicipalitiesLazy, useMunicipalityLazy } from "../../hooks/municipalities"
import { useCreateNamedEntity, useNamedEntitiesLazy, useNamedEntityLazy } from "../../hooks/named-entities"
import { useCreateResource, useResourceLazy, useResourcesLazy } from "../../hooks/resources"
import type { Document, NamedEntity } from "../../types"
import { transformErrorForForm } from "../../utils/utils"
import KeywordModal from "../Keywords/KeywordModal"
import NamedEntityModal from "../NamedEntities/NamedEntityModal"
import ResourceModal from "../Resources/ResourceModal"
import { useDocumentLazy, useDocumentsLazy } from "../../hooks/documents"

export default function DocumentForm({ col = 8 }: { col?: number }) {
  const { setFieldValue, values, errors, submitCount } = useFormikContext<Document>()

  const fetchNamedEntity = useNamedEntityLazy()
  const fetchNamedEntitiesAuthors = useNamedEntitiesLazy({ entity_type: "AUTHOR" })
  const fetchNamedEntitiesPhotographers = useNamedEntitiesLazy({ entity_type: "PHOTOGRAPHER" })
  const fetchNamedEntitiesDesigners = useNamedEntitiesLazy({ entity_type: "DESIGNER" })
  const fetchNamedEntitiesEditors = useNamedEntitiesLazy({ entity_type: "EDITOR" })
  const fetchNamedEntitiesTipologies = useNamedEntitiesLazy({ entity_type: "TYPOLOGY" })
  const fetchNamedEntitiesFormats = useNamedEntitiesLazy({ entity_type: "FORMAT" })
  const fetchNamedEntitiesCollections = useNamedEntitiesLazy({ entity_type: "COLLECTION" })
  const fetchDocuments = useDocumentsLazy()
  const fetchMunicipality = useMunicipalityLazy()
  const fetchMunicipalities = useMunicipalitiesLazy()
  const fetchKeyword = useKeywordLazy()
  const fetchKeywords = useKeywordsLazy()
  const fetchResources = useResourcesLazy()
  const fetchResource = useResourceLazy()
  const fetchDocument = useDocumentLazy()

  const createNamedEntity = useCreateNamedEntity()
  const createKeyword = useCreateKeyword()
  const createResource = useCreateResource()

  const [modalStateCreateNamedEntity, modalActionsCreateNamedEntity] = useModalTrigger()
  const [modalStateCreateKeyword, modalActionsCreateKeyword] = useModalTrigger()
  const [modalStateCreateResource, modalActionsCreateResource] = useModalTrigger()

  return (
    <Col xs={col} className="d-flex flex-column align-items-stretch gap-4">
      <CharField
        label="Titolo"
        layout="horizontal"
        value={values.title}
        onChange={(value) => {
          setFieldValue("title", value)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "title") : undefined}
        errorMessage={getIn(errors, "title")}
        required
      />
      <WysiwygField
        label="Descrizione"
        layout="horizontal"
        value={values.description || ""}
        onChange={(value) => {
          setFieldValue("description", value)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "description") : undefined}
        errorMessage={getIn(errors, "description")}
        required
      />
      <CheckboxField
        label="Evidenziato"
        layout="horizontal"
        value={values.highlighted || false}
        onChange={(checked) => {
          setFieldValue("highlighted", checked)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "highlighted") : undefined}
        errorMessage={getIn(errors, "highlighted")}
      />
      <AsyncMultiSelectField
        label="Scrittori"
        layout="horizontal"
        placeholder="Seleziona uno o più scrittori"
        onChange={(value) => {
          setFieldValue("authors", value)
        }}
        clearable
        value={values.authors || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(
            ids?.map((id) => fetchNamedEntity(id).then((author) => ({ label: author.name, value: author.id }))),
          )
        }
        loadOptions={(inputValue: string) => {
          return fetchNamedEntitiesAuthors({ search: inputValue }).then((authors) => {
            return authors.results?.map((author) => ({
              label: author.name,
              value: author.id,
            }))
          })
        }}
      >
        <AsyncMultiSelectField.Add
          onClick={() => {
            modalActionsCreateNamedEntity.open("AUTHOR")
          }}
        />
      </AsyncMultiSelectField>
      <AsyncMultiSelectField
        label="Case editrici"
        layout="horizontal"
        placeholder="Seleziona uno o più case editrici"
        onChange={(value) => {
          setFieldValue("editors", value)
        }}
        clearable
        value={values.editors || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(
            ids?.map((id) => fetchNamedEntity(id).then((editor) => ({ label: editor.name, value: editor.id }))),
          )
        }
        loadOptions={(inputValue: string) => {
          return fetchNamedEntitiesEditors({ search: inputValue }).then((editors) => {
            return editors.results?.map((editor) => ({
              label: editor.name,
              value: editor.id,
            }))
          })
        }}
      >
        <AsyncMultiSelectField.Add
          onClick={() => {
            modalActionsCreateNamedEntity.open("EDITOR")
          }}
        />
      </AsyncMultiSelectField>

      <AsyncMultiSelectField
        label="Fotografi"
        layout="horizontal"
        placeholder="Seleziona uno o più fotografi"
        onChange={(value) => {
          setFieldValue("photographers", value)
        }}
        clearable
        value={values.photographers || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(
            ids?.map((id) =>
              fetchNamedEntity(id).then((photographer) => ({ label: photographer.name, value: photographer.id })),
            ),
          )
        }
        loadOptions={(inputValue: string) => {
          return fetchNamedEntitiesPhotographers({ search: inputValue }).then((photographers) => {
            return photographers.results?.map((photographer) => ({
              label: photographer.name,
              value: photographer.id,
            }))
          })
        }}
      >
        <AsyncMultiSelectField.Add
          onClick={() => {
            modalActionsCreateNamedEntity.open("PHOTOGRAPHER")
          }}
        />
      </AsyncMultiSelectField>

      <AsyncMultiSelectField
        label="Curatori e Designer"
        layout="horizontal"
        placeholder="Seleziona uno o più curatori e designer"
        onChange={(value) => {
          setFieldValue("designers", value)
        }}
        clearable
        value={values.designers || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(
            ids?.map((id) => fetchNamedEntity(id).then((designer) => ({ label: designer.name, value: designer.id }))),
          )
        }
        loadOptions={(inputValue: string) => {
          return fetchNamedEntitiesDesigners({ search: inputValue }).then((designers) => {
            return designers.results?.map((designer) => ({
              label: designer.name,
              value: designer.id,
            }))
          })
        }}
      >
        <AsyncMultiSelectField.Add
          onClick={() => {
            modalActionsCreateNamedEntity.open("DESIGNER")
          }}
        />
      </AsyncMultiSelectField>

      <AsyncMultiSelectField
        label="Collane"
        layout="horizontal"
        placeholder="Seleziona una o più collane"
        onChange={(value) => {
          setFieldValue("collections", value)
        }}
        clearable
        value={values.collections || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(
            ids?.map((id) =>
              fetchNamedEntity(id).then((collection) => ({ label: collection.name, value: collection.id })),
            ),
          )
        }
        loadOptions={(inputValue: string) => {
          return fetchNamedEntitiesCollections({ search: inputValue }).then((collections) => {
            return collections.results?.map((collection) => ({
              label: collection.name,
              value: collection.id,
            }))
          })
        }}
      >
        <AsyncMultiSelectField.Add
          onClick={() => {
            modalActionsCreateNamedEntity.open("COLLECTION")
          }}
        />
      </AsyncMultiSelectField>

      <AsyncMultiSelectField
        label="Tipologie"
        layout="horizontal"
        placeholder="Seleziona una o più tipologie"
        onChange={(value) => {
          setFieldValue("typologies", value)
        }}
        clearable
        value={values.typologies || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(
            ids?.map((id) => fetchNamedEntity(id).then((typology) => ({ label: typology.name, value: typology.id }))),
          )
        }
        loadOptions={(inputValue: string) => {
          return fetchNamedEntitiesTipologies({ search: inputValue }).then((tipologies) => {
            return tipologies.results?.map((tipology) => ({
              label: tipology.name,
              value: tipology.id,
            }))
          })
        }}
      >
        <AsyncMultiSelectField.Add
          onClick={() => {
            modalActionsCreateNamedEntity.open("TYPOLOGY")
          }}
        />
      </AsyncMultiSelectField>

      <AsyncMultiSelectField
        label="Formati"
        layout="horizontal"
        placeholder="Seleziona uno o più formati"
        onChange={(value) => {
          setFieldValue("formats", value)
        }}
        clearable
        value={values.formats || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(
            ids?.map((id) => fetchNamedEntity(id).then((format) => ({ label: format.name, value: format.id }))),
          )
        }
        loadOptions={(inputValue: string) => {
          return fetchNamedEntitiesFormats({ search: inputValue }).then((formats) => {
            return formats.results?.map((format) => ({
              label: format.name,
              value: format.id,
            }))
          })
        }}
      >
        <AsyncMultiSelectField.Add
          onClick={() => {
            modalActionsCreateNamedEntity.open("FORMAT")
          }}
        />
      </AsyncMultiSelectField>

      <AsyncMultiSelectField
        label="Temi"
        layout="horizontal"
        placeholder="Seleziona uno o più temi"
        onChange={(value) => {
          setFieldValue("keywords", value)
        }}
        clearable
        value={values.keywords || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(
            ids?.map((id) => fetchKeyword(id).then((keyword) => ({ label: keyword.name, value: keyword.id }))),
          )
        }
        loadOptions={(inputValue: string) => {
          return fetchKeywords({ search: inputValue }).then((keywords) => {
            return keywords.results?.map((keyword) => ({
              label: keyword.name,
              value: keyword.id,
            }))
          })
        }}
      >
        <AsyncMultiSelectField.Add
          onClick={() => {
            modalActionsCreateKeyword.open()
          }}
        />
      </AsyncMultiSelectField>

      <CharField
        label="Anno"
        layout="horizontal"
        value={values.year ? values.year.toString() : ""}
        onChange={(value) => {
          setFieldValue("year", value ? Number(value) : undefined)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "year") : undefined}
        errorMessage={getIn(errors, "year")}
        required
      />
      <CharField
        label="Data generica"
        layout="horizontal"
        value={values.date_generic || ""}
        onChange={(value) => {
          setFieldValue("date_generic", value)
        }}
        isValid={submitCount > 0 ? !getIn(errors, "date_generic") : undefined}
        errorMessage={getIn(errors, "date_generic")}
      />

      <FieldArray name="source_urls">
        {({ push, remove }) => (
          <>
            {(values.source_urls || []).map((source_url, index) => (
              <>
                <LinkField
                  layout="horizontal"
                  label="Link fonte"
                  inputColSize={8}
                  value={source_url}
                  onChange={(value) => {
                    setFieldValue(`source_urls.${index}`, value)
                  }}
                  isValid={submitCount > 0 ? !getIn(errors, `source_urls.${index}`) : undefined}
                  errorMessage={getIn(errors, `source_urls.${index}`)}
                />
                <Row>
                  <Col md={4} />
                  <Col md={8}>
                    <button
                      type="button"
                      style={{ width: "fit-content" }}
                      className="btn btn-danger"
                      onClick={() => {
                        remove(index)
                      }}
                    >
                      Rimuovi
                    </button>
                  </Col>
                </Row>
              </>
            ))}
            <Row>
              <Col md={4} />
              <Col md={8}>
                <button
                  type="button"
                  className="btn btn-primary mt-2"
                  onClick={() => {
                    push("")
                  }}
                >
                  Aggiungi link di fonte
                </button>
              </Col>
            </Row>
          </>
        )}
      </FieldArray>

      <AsyncMultiSelectField
        label="Comuni"
        layout="horizontal"
        placeholder="Seleziona uno o più comuni"
        onChange={(value) => {
          setFieldValue("municipalities", value)
        }}
        clearable
        value={values.municipalities || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(
            ids?.map((id) =>
              fetchMunicipality(id).then((municipality) => ({ label: municipality.name, value: municipality.id })),
            ),
          )
        }
        loadOptions={(inputValue: string) => {
          return fetchMunicipalities({ search: inputValue }).then((municipalities) => {
            return municipalities.results?.map((municipality) => ({
              label: municipality.name,
              value: municipality.id,
            }))
          })
        }}
      />
      <AsyncMultiSelectField
        label="Risorse"
        layout="horizontal"
        placeholder="Seleziona uno o più risorse"
        onChange={(value) => {
          setFieldValue("resources", value)
        }}
        clearable
        value={values.resources || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(
            ids?.map((id) => fetchResource(id).then((resource) => ({ label: resource.title, value: resource.id }))),
          )
        }
        loadOptions={(inputValue: string) => {
          return fetchResources({ search: inputValue }).then((resources) => {
            return resources.results?.map((resource) => ({
              label: resource.title,
              value: resource.id,
            }))
          })
        }}
      >
        <AsyncMultiSelectField.Add
          onClick={() => {
            modalActionsCreateResource.open()
          }}
        />
      </AsyncMultiSelectField>

      <AsyncMultiSelectField
        label="Fototesti correlati"
        layout="horizontal"
        placeholder="Seleziona uno o più fototesti correlati"
        onChange={(value) => {
          setFieldValue("related_records", value)
        }}
        clearable
        value={values.related_records || []}
        loadInitialValues={(ids: number[]) =>
          Promise.all(
            ids?.map((id) => fetchDocument(id).then((document) => ({ label: document.title, value: document.id }))),
          )
        }
        loadOptions={(inputValue: string) => {
          return fetchDocuments({ search: inputValue }).then((documents) => {
            return documents.results?.map((document) => ({
              label: document.title,
              value: document.id,
            }))
          })
        }}
      />

      <SwitchField
        label="Bozza"
        layout="horizontal"
        value={values.draft || false}
        onChange={(checked) => {
          setFieldValue("draft", checked)
        }}
      />

      {modalStateCreateNamedEntity.isOpen && (
        <NamedEntityModal
          show={modalStateCreateNamedEntity.isOpen}
          type={modalStateCreateNamedEntity.value}
          onClose={modalActionsCreateNamedEntity.close}
          save={(valuesEntity, actions) => {
            return createNamedEntity
              .mutateAsync(valuesEntity)
              .then((namedEntity: NamedEntity) => {
                if (valuesEntity.entity_types.includes("EDITOR"))
                  setFieldValue("editors", [...((values.editors as number[]) || []), namedEntity.id])
                else if (valuesEntity.entity_types.includes("PHOTOGRAPHER"))
                  setFieldValue("photographers", [...((values.photographers as number[]) || []), namedEntity.id])
                else if (valuesEntity.entity_types.includes("FORMAT"))
                  setFieldValue("formats", [...((values.formats as number[]) || []), namedEntity.id])
                else if (valuesEntity.entity_types.includes("TYPOLOGY"))
                  setFieldValue("typologies", [...((values.typologies as number[]) || []), namedEntity.id])
                else if (valuesEntity.entity_types.includes("AUTHOR"))
                  setFieldValue("authors", [...((values.authors as number[]) || []), namedEntity.id])
                else if (valuesEntity.entity_types.includes("COLLECTION"))
                  setFieldValue("collections", [...((values.collections as number[]) || []), namedEntity.id])

                modalActionsCreateNamedEntity.close()
              })
              .catch((e: any) => {
                actions.setErrors(transformErrorForForm(e))
              })
          }}
          title={
            modalStateCreateNamedEntity.value === "AUTHOR"
              ? "Crea nuovo scrittore"
              : modalStateCreateNamedEntity.value === "EDITOR"
                ? "Crea nuova casa editrice"
                : modalStateCreateNamedEntity.value === "PHOTOGRAPHER"
                  ? "Crea nuovo fotografo"
                  : modalStateCreateNamedEntity.value === "TYPOLOGY"
                    ? "Crea nuova tipologia"
                    : modalStateCreateNamedEntity.value === "COLLECTION"
                      ? "Crea nuova collana"
                      : "Crea nuovo formato"
          }
        />
      )}
      {modalStateCreateKeyword.isOpen && (
        <KeywordModal
          show={modalStateCreateKeyword.isOpen}
          onClose={modalActionsCreateKeyword.close}
          save={(valuesKeyword, actions) => {
            return createKeyword
              .mutateAsync(valuesKeyword)
              .then((keyword) => {
                setFieldValue("keywords", [...((values.keywords as number[]) || []), keyword.id])
                modalActionsCreateKeyword.close()
              })
              .catch((e: any) => {
                actions.setErrors(transformErrorForForm(e))
              })
          }}
          title="Crea nuovo tema"
        />
      )}
      {modalStateCreateResource.isOpen && (
        <ResourceModal
          show={modalStateCreateResource.isOpen}
          onClose={modalActionsCreateResource.close}
          save={(valuesResource, actions) => {
            return createResource
              .mutateAsync(valuesResource)
              .then((resource) => {
                setFieldValue("resources", [...((values.resources as number[]) || []), resource.id])
                modalActionsCreateResource.close()
              })
              .catch((e: any) => {
                actions.setErrors(transformErrorForForm(e))
              })
          }}
          title="Crea nuova risorsa"
        />
      )}
    </Col>
  )
}
